import { useEffect } from "react";
import { motion } from "framer-motion";
import { 
  PackageSearch, ShieldCheck, Truck, Scale, IndianRupee, Barcode, 
  FileSpreadsheet, Activity, Users, Layers, MapPin, Clock, ArrowRight,
  LogIn
} from "lucide-react";

/**
 * WECARE Landing Page
 * -------------------------------------------------------------
 * Drop this file as src/pages/LandingPage.jsx (or replace your current landing route)
 * Assumes TailwindCSS is configured in the project.
 * Uses framer-motion + lucide-react icons (install if needed):
 *   npm i framer-motion lucide-react
 */
export default function LandingPage() {
  useEffect(() => {
    document.title = "WECARE India – Pharma ERP for Distributors, C&F, Stockists";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-900 via-sky-800 to-sky-900 text-white">
      <Navbar />
      <Hero />
      <TrustBar />
      <FeatureGrid />
      <SolutionStrips />
      <WhyUs />
      <CTA />
      <Footer />
    </div>
  );
}

function Container({ children, className = "" }) {
  return (
    <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
}

function Navbar() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-sky-900/60 bg-sky-900/70 border-b border-white/10">
      <Container className="flex h-16 items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-white/10 grid place-items-center ring-1 ring-white/15">
            <Layers className="h-5 w-5" />
          </div>
          <div>
            <p className="text-lg font-bold leading-tight">WECARE <span className="text-sky-300">India</span></p>
            <p className="text-xs text-white/70 -mt-1">Pharma / Medical ERP</p>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a href="#features" className="hover:text-sky-200">Features</a>
          <a href="#solutions" className="hover:text-sky-200">Solutions</a>
          <a href="#why" className="hover:text-sky-200">Why WECARE</a>
          <a href="#contact" className="hover:text-sky-200">Contact</a>
        </nav>

        <div className="flex items-center gap-3">
          <a href="/login" className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/15 hover:bg-white/15">
            <LogIn className="h-4 w-4" /> Login
          </a>
          <a href="#demo" className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-sky-300 text-sky-950 px-4 py-2 text-sm font-semibold hover:bg-sky-200 shadow">
            Request Demo <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </Container>
    </header>
  );
}

function Hero() {
  return (
    <Container className="pt-16 pb-8 lg:pt-24 lg:pb-16">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight"
          >
            Software for Pharma / Medical <span className="text-sky-300">Wholesalers, Distributors, C&F</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed"
          >
            Manage distribution, consignment, super-stockists and chains with ease. Batch & expiry,
            GST/e-Invoice, doctor commissions, purchase & sales, and intelligent reporting — all in one powerful,
            India‑ready suite.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a href="/login" className="inline-flex items-center gap-2 rounded-2xl bg-sky-300 text-sky-950 px-5 py-3 font-semibold hover:bg-sky-200 shadow">
              <LogIn className="h-5 w-5"/> Start Now
            </a>
            <a href="#features" className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold ring-1 ring-white/15 hover:bg-white/15">
              Explore Features
            </a>
          </motion.div>

          <div className="mt-8 grid grid-cols-3 gap-6 text-sm text-white/80">
            <Stat kpi="99.95%" label="Uptime" />
            <Stat kpi="10,000+" label="Batches Managed" />
            <Stat kpi="GST-Ready" label="E‑Invoice & e‑Way Bill" />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="relative"
        >
          <div className="absolute inset-0 -translate-x-6 -translate-y-6 blur-3xl opacity-30 bg-gradient-to-tr from-sky-400 to-indigo-300 rounded-3xl" />
          <div className="relative rounded-3xl bg-white/5 ring-1 ring-white/10 p-4 sm:p-6 shadow-xl">
            <HeroCard />
          </div>
        </motion.div>
      </div>
    </Container>
  );
}

function Stat({ kpi, label }) {
  return (
    <div>
      <p className="text-xl font-bold text-sky-200">{kpi}</p>
      <p className="text-white/70">{label}</p>
    </div>
  );
}

function HeroCard() {
  const row = "flex items-center justify-between rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3";
  const chip = "rounded-full bg-sky-300/20 text-sky-100 text-xs px-3 py-1 ring-1 ring-sky-300/30";
  return (
    <div className="space-y-3">
      <div className={`${row}`}>
        <div className="flex items-center gap-3">
          <Barcode className="h-5 w-5" />
          <p className="font-semibold">Batch & Expiry Tracking</p>
        </div>
        <span className={chip}>Auto Alerts</span>
      </div>
      <div className={`${row}`}>
        <div className="flex items-center gap-3">
          <FileSpreadsheet className="h-5 w-5" />
          <p className="font-semibold">GST / e-Invoice</p>
        </div>
        <span className={chip}>India Ready</span>
      </div>
      <div className={`${row}`}>
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5" />
          <p className="font-semibold">Doctor Commissions</p>
        </div>
        <span className={chip}>Flexible Rules</span>
      </div>
      <div className={`${row}`}>
        <div className="flex items-center gap-3">
          <Truck className="h-5 w-5" />
          <p className="font-semibold">Dispatch & e‑Way Bill</p>
        </div>
        <span className={chip}>Fast</span>
      </div>
    </div>
  );
}

function TrustBar() {
  return (
    <Container className="py-10">
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 sm:p-8">
        <p className="text-center text-sm tracking-wide text-white/70">
          Inspired by leading Indian pharma software ecosystems you already trust.
        </p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-6 opacity-80">
          {[
            "Distribution",
            "C&F",
            "Super‑Stockist",
            "Chain Depots",
          ].map((t) => (
            <div key={t} className="text-center">
              <span className="text-white/80 text-sm">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
}

function FeatureGrid() {
  const items = [
    { icon: PackageSearch, title: "Inventory with Packs", desc: "Strip/bottle/pack wise with conversions and MRP/Rate locks." },
    { icon: Barcode, title: "Batch & Expiry", desc: "Auto‑FIFO, near‑expiry alerts, returns & replacements." },
    { icon: FileSpreadsheet, title: "GST & e‑Invoice", desc: "IRN, e‑Way Bill data prep, HSN & tax categories." },
    { icon: IndianRupee, title: "Doctor Commission", desc: "Customer/doctor linkage, product‑wise splits and exclusions." },
    { icon: ShieldCheck, title: "Role Security", desc: "Sales, Warehouse, CRM, Admin — least‑privilege out of the box." },
    { icon: Activity, title: "Analytics", desc: "Dashboards for sales, dues, profitability and route KPIs." },
  ];
  return (
    <Container id="features" className="py-16">
      <h2 className="text-center text-2xl sm:text-3xl font-bold">Everything you expect from a modern <span className="text-sky-300">Pharma ERP</span></h2>
      <p className="mt-3 text-center text-white/80 max-w-3xl mx-auto">
        Designed for Indian workflows and compliance. Integrates purchase → stock → order → invoice → dispatch seamlessly.
      </p>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <motion.div key={it.title} initial={{opacity:0, y:10}} whileInView={{opacity:1, y:0}} viewport={{once:true}} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 hover:bg-white/10 transition">
            <div className="flex items-center gap-3">
              <it.icon className="h-6 w-6 text-sky-200" />
              <p className="font-semibold">{it.title}</p>
            </div>
            <p className="mt-2 text-sm text-white/80">{it.desc}</p>
          </motion.div>
        ))}
      </div>
    </Container>
  );
}

function SolutionStrips() {
  const strips = [
    { icon: Truck, title: "Distribution & C&F", points: ["Multiple depots", "Consignment & transfers", "Delivery & transport"] },
    { icon: MapPin, title: "Salesperson App", points: ["Visits with GPS", "Route plans", "On‑device stock & order"] },
    { icon: Clock, title: "Batch‑First Workflows", points: ["Purchase‑linked batch create", "FIFO picking", "Expiry‑aware returns"] },
    { icon: Scale, title: "Reconciliation", points: ["Outstanding & statements", "Credit notes", "GST reports"] },
  ];

  return (
    <Container id="solutions" className="py-4 pb-16">
      <div className="grid gap-4 lg:grid-cols-2">
        {strips.map((s) => (
          <div key={s.title} className="rounded-2xl bg-gradient-to-br from-sky-700/60 to-sky-900/60 ring-1 ring-white/10 p-6">
            <div className="flex items-center gap-3">
              <s.icon className="h-6 w-6" />
              <h3 className="font-semibold text-lg">{s.title}</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-white/85 list-disc list-inside">
              {s.points.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </Container>
  );
}

function WhyUs() {
  const bullets = [
    "Fast UI inspired by proven Indian pharma tools.",
    "Low training time — sensible defaults for packs, HSN, tax.",
    "Built for both desktop and mobile teams.",
    "Modular — start small: Inventory → Orders → Invoicing → GST.",
  ];
  return (
    <Container id="why" className="py-16">
      <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-8 lg:p-12 grid lg:grid-cols-2 gap-8">
        <div>
          <h3 className="text-2xl font-bold">Why teams switch to <span className="text-sky-300">WECARE</span></h3>
          <p className="mt-3 text-white/80">We’ve modelled the workflows around Indian distributors and C&F agents so you don’t have to fight the software.</p>
          <ul className="mt-6 space-y-3 text-sm text-white/85 list-disc list-inside">
            {bullets.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
            <p className="text-3xl font-extrabold text-sky-200"><span className="align-top text-base mr-1">↑</span>35%</p>
            <p className="text-sm text-white/80">Faster invoice creation</p>
          </div>
          <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
            <p className="text-3xl font-extrabold text-sky-200">0</p>
            <p className="text-sm text-white/80">Duplicate batches (with our validations)</p>
          </div>
          <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
            <p className="text-3xl font-extrabold text-sky-200">Realtime</p>
            <p className="text-sm text-white/80">Stock, dues & GST visibility</p>
          </div>
          <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
            <p className="text-3xl font-extrabold text-sky-200">+ROI</p>
            <p className="text-sm text-white/80">Doctor commission tracking</p>
          </div>
        </div>
      </div>
    </Container>
  );
}

function CTA() {
  return (
    <Container id="demo" className="py-16">
      <div className="rounded-3xl bg-gradient-to-r from-sky-300 to-cyan-300 text-sky-950 p-8 lg:p-12 shadow-xl">
        <div className="grid lg:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="text-2xl sm:text-3xl font-extrabold">Ready to modernize your distribution?</h3>
            <p className="mt-2 text-sky-900/80">Get a guided demo mapped to your exact workflow — purchase, batches, orders, invoicing and GST.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <a href="/login" className="inline-flex items-center gap-2 rounded-2xl bg-sky-950 text-white px-5 py-3 font-semibold hover:opacity-90">
              Login Now <ArrowRight className="h-5 w-5"/>
            </a>
            <a href="#contact" className="inline-flex items-center gap-2 rounded-2xl bg-white text-sky-950 px-5 py-3 font-semibold hover:bg-slate-100">
              Talk to Us
            </a>
          </div>
        </div>
      </div>
    </Container>
  );
}

function Footer() {
  return (
    <footer id="contact" className="mt-16 border-t border-white/10 bg-sky-950/40">
      <Container className="py-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-sm">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-white/10 grid place-items-center ring-1 ring-white/15">
              <Layers className="h-5 w-5" />
            </div>
            <p className="text-lg font-bold">WECARE India</p>
          </div>
          <p className="mt-3 text-white/70">Pharma/Medical software built for Indian distributors, C&F and super‑stockists.</p>
        </div>

        <div>
          <p className="font-semibold mb-2">Product</p>
          <ul className="space-y-2 text-white/80">
            <li><a href="#features" className="hover:text-white">Features</a></li>
            <li><a href="#solutions" className="hover:text-white">Solutions</a></li>
            <li><a href="#why" className="hover:text-white">Why WECARE</a></li>
          </ul>
        </div>

        <div>
          <p className="font-semibold mb-2">Company</p>
          <ul className="space-y-2 text-white/80">
            <li><a href="#contact" className="hover:text-white">Contact</a></li>
            <li><a href="#demo" className="hover:text-white">Request Demo</a></li>
          </ul>
        </div>

        <div>
          <p className="font-semibold mb-2">Contact</p>
          <ul className="space-y-2 text-white/80">
            <li>sales@wecareindia.example</li>
            <li>+91‑98XXXXXX00</li>
            <li>Ahmedabad · Mumbai · Delhi</li>
          </ul>
        </div>
      </Container>
      <Container>
        <div className="py-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <p>© {new Date().getFullYear()} WECARE India. All rights reserved.</p>
          <p>Made for distributors, stockists & C&F — India ready.</p>
        </div>
      </Container>
    </footer>
  );
}
